/**
 * Paper Trading API Service
 *
 * This service handles all API calls related to paper trading functionality
 * including mode switching, account management, and trade operations.
 */

import { API_BASE_URL } from '../config';
import { cachedGet, requestCache, cachedPost } from '@/utils/cache';
import type { CacheOptions } from '@/utils/cache';

const PAPER_TRADING_API_URL = `${API_BASE_URL}/api/paper-trading`;

interface PaperTradingMode {
  success: boolean;
  mode: 'paper' | 'live';
  paper_trading_enabled: boolean;
}

interface PaperAccount {
  success: boolean;
  account: {
    id: string;
    user_id: string;
    virtual_balance: number;
    initial_balance: number;
    total_pnl: number;
    total_trades_count: number;
    winning_trades_count: number;
    losing_trades_count: number;
    win_rate: number;
    reset_count: number;
    last_reset_at: string | null;
    created_at: string;
    updated_at: string;
  };
  reset_info?: {
    todays_resets: number;
    resets_remaining: number;
    max_resets_per_day: number;
    can_reset_today: boolean;
  };
  performance: {
    total_return_percentage: number;
    recent_trades_count: number;
    account_age_days: number;
  };
}

interface PaperTradingHistory {
  success: boolean;
  trades: Array<{
    id: string;
    symbol: string;
    side: string;
    quantity: number;
    entry_price: number;
    exit_price: number | null;
    pnl: number | null;
    status: string;
    entry_time: string;
    exit_time: string | null;
  }>;
  pagination: {
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
  };
}



class PaperTradingApiService {
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('access_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Network error' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }
    return response.json();
  }

  /**
   * Get current trading mode (paper or live)
   */
  async getTradingMode(): Promise<PaperTradingMode> {
    return cachedGet<PaperTradingMode>(`${PAPER_TRADING_API_URL}/mode`, { ttl: 60000 });
  }

  /**
   * Switch between paper and live trading modes
   */
  async switchTradingMode(paperMode: boolean): Promise<{ success: boolean; mode: string; message: string }> {
    const result = await cachedPost<{ success: boolean; mode: string; message: string }>(
      `${PAPER_TRADING_API_URL}/mode`,
      { paper_mode: paperMode },
      { debounce: 1000, deduplicate: true }
    );

    requestCache.invalidate('/api/paper-trading/mode');
    requestCache.invalidate('/api/paper-trading/account');
    requestCache.invalidate('/api/dashboard/batch');

    return result;
  }

  /**
   * Get paper trading account summary
   */
  async getPaperAccount(options?: CacheOptions): Promise<PaperAccount> {
    return cachedGet<PaperAccount>(`${PAPER_TRADING_API_URL}/account`, { ttl: 5000, ...options });
  }

  /**
   * Get paper trading statistics (optimized for dashboard)
   */
  async getPaperTradingStatistics(options?: CacheOptions): Promise<{
    success: boolean;
    total_pnl: number;
    win_rate: number;
    monthly_rate: number;
    total_trades: number;
  }> {
    return cachedGet(`${PAPER_TRADING_API_URL}/statistics`, { ttl: 5000, ...options });
  }

  /**
   * Reset paper trading account
   */
  async resetPaperAccount(newBalance?: number): Promise<{ success: boolean; message: string; new_balance: number; reset_count: number }> {
    const response = await fetch(`${PAPER_TRADING_API_URL}/account/reset`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(newBalance ? { new_balance: newBalance } : {})
    });
    const result = await this.handleResponse<{ success: boolean; message: string; new_balance: number; reset_count: number }>(response);

    requestCache.invalidate('/api/paper-trading/*');
    requestCache.invalidate('/api/dashboard/batch');

    return result;
  }

  /**
   * Get paper trading history with pagination
   */
  async getPaperTradingHistory(limit: number = 50, offset: number = 0, options?: { signal?: AbortSignal }): Promise<PaperTradingHistory> {
    return cachedGet<PaperTradingHistory>(`${PAPER_TRADING_API_URL}/history?limit=${limit}&offset=${offset}`, {
      ttl: 10000,
      signal: options?.signal,
    });
  }



  /**
   * Create a new paper trading session
   */
  async createPaperTradingSession(symbol: string = 'BTCUSDT', leverage: number = 1, investmentPercentage: number = 0): Promise<{ success: boolean; session: any }> {
    const response = await fetch(`${PAPER_TRADING_API_URL}/session`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        symbol,
        leverage,
        investment_percentage: investmentPercentage
      })
    });
    return this.handleResponse<{ success: boolean; session: any }>(response);
  }

  /**
   * Get current paper trading positions
   */
  async getPaperPositions(options?: CacheOptions): Promise<{ success: boolean; positions: any[] }> {
    const cacheOptions: CacheOptions = { ttl: 30000, ...options };
    const forceRefresh = cacheOptions.forceRefresh === true;

    try {
      const response = await cachedGet<{ success: boolean; positions: any[] }>(
        `${PAPER_TRADING_API_URL}/positions`,
        cacheOptions
      );

      if (!response || typeof response !== 'object') {
        throw new Error('Invalid positions response: not an object');
      }

      if (!Array.isArray(response.positions)) {
        throw new Error('Invalid positions response: positions not an array');
      }

      if (response.success !== true) {
        throw new Error('Positions response indicates failure');
      }

      console.debug('[paperTradingApi] getPaperPositions resolved', {
        forceRefresh,
        count: response.positions.length
      });

      return response;
    } catch (error) {
      console.error('[paperTradingApi] getPaperPositions failed', {
        forceRefresh,
        error
      });
      throw error;
    }
  }

  /**
   * Paper Trading Risk Settings - Get
   */
  async getPaperRiskSettings(options?: CacheOptions): Promise<{
    success: boolean;
    investment_percentage: number;
    leverage: number;
    max_stop_loss_usd?: number | null;
    is_configured: boolean;
    grid_enabled?: boolean;
    grid_levels?: number;
    grid_sizing_mode?: 'equal' | 'scaled' | string;
    grid_scale_multiplier?: number;
    grid_strategy_mode?: 'manual' | 'ai_auto' | string;
    grid_lower_price?: number | null;
    grid_upper_price?: number | null;
    grid_recommendation_meta?: any;
    trailing_stop_enabled?: boolean;
    selected_trading_symbols?: string[] | null;
    available_symbols?: string[];
  }> {
    return cachedGet(`${PAPER_TRADING_API_URL}/risk-settings`, { ttl: 300000, ...options });
  }

  /**
   * Paper Trading Risk Settings - Save
   */
  async savePaperRiskSettings(
    investment_percentage: number,
    leverage: number,
    max_stop_loss_usd?: number | null,
    grid_settings?: {
      grid_enabled?: boolean;
      grid_levels?: number;
      grid_sizing_mode?: 'equal' | 'scaled' | string;
      grid_scale_multiplier?: number;
      grid_strategy_mode?: 'manual' | 'ai_auto' | string;
      grid_lower_price?: number | null;
      grid_upper_price?: number | null;
      grid_recommendation_meta?: any;
      trailing_stop_enabled?: boolean;
      selected_trading_symbols?: string[] | null;
    }
  ): Promise<{
    success: boolean;
    settings: {
      investment_percentage: number;
      leverage: number;
      max_stop_loss_usd?: number | null;
      is_configured: boolean;
      grid_enabled?: boolean;
      grid_levels?: number;
      grid_sizing_mode?: 'equal' | 'scaled' | string;
      grid_scale_multiplier?: number;
      grid_strategy_mode?: 'manual' | 'ai_auto' | string;
      grid_lower_price?: number | null;
      grid_upper_price?: number | null;
      grid_recommendation_meta?: any;
      trailing_stop_enabled?: boolean;
    }
  }> {
    const response = await fetch(`${PAPER_TRADING_API_URL}/risk-settings`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ investment_percentage, leverage, max_stop_loss_usd, ...(grid_settings || {}) })
    });
    const result = await this.handleResponse<{
      success: boolean;
      settings: {
        investment_percentage: number;
        leverage: number;
        max_stop_loss_usd?: number | null;
        is_configured: boolean;
        grid_enabled?: boolean;
        grid_levels?: number;
        grid_sizing_mode?: 'equal' | 'scaled' | string;
        grid_scale_multiplier?: number;
        trailing_stop_enabled?: boolean;
      }
    }>(response);

    requestCache.invalidate('/api/paper-trading/risk-settings');

    return result;
  }

  /**
   * Close a specific paper trading position
   */
  async closePaperPosition(positionId: string, reason: string = 'manual'): Promise<{ success: boolean; trade_id: string; exit_price: string; pnl: string; exit_reason: string }> {
    const response = await fetch(`${PAPER_TRADING_API_URL}/positions/${positionId}/close`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ reason })
    });
    const result = await this.handleResponse<{ success: boolean; trade_id: string; exit_price: string; pnl: string; exit_reason: string }>(response);

    requestCache.invalidate('/api/paper-trading/positions');
    requestCache.invalidate('/api/paper-trading/history');
    requestCache.invalidate('/api/paper-trading/account');
    requestCache.invalidate('/api/dashboard/batch');

    return result;
  }

  /**
   * Get current paper trading balance
   */
  async getPaperBalance(options?: CacheOptions): Promise<{ success: boolean; balance: number; currency: string }> {
    return cachedGet(`${PAPER_TRADING_API_URL}/balance`, { ttl: 5000, ...options });
  }

  /**
   * Set Take Profit for a paper trading position
   */
  async setTakeProfit(positionId: string, takeProfitPrice: number): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${PAPER_TRADING_API_URL}/positions/${positionId}/take-profit`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ take_profit_price: takeProfitPrice })
    });
    const result = await this.handleResponse<{ success: boolean; message: string }>(response);

    requestCache.invalidate('/api/paper-trading/positions');

    return result;
  }

  /**
   * Set Stop Loss for a paper trading position
   */
  async setStopLoss(positionId: string, stopLossPrice: number): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${PAPER_TRADING_API_URL}/positions/${positionId}/stop-loss`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ stop_loss_price: stopLossPrice })
    });
    const result = await this.handleResponse<{ success: boolean; message: string }>(response);

    requestCache.invalidate('/api/paper-trading/positions');

    return result;
  }

  /**
   * Reverse a paper trading position
   */
  async reversePosition(positionId: string): Promise<{ success: boolean; message: string; new_position_id?: string }> {
    const response = await fetch(`${PAPER_TRADING_API_URL}/positions/${positionId}/reverse`, {
      method: 'POST',
      headers: this.getAuthHeaders()
    });
    return this.handleResponse<{ success: boolean; message: string; new_position_id?: string }>(response);
  }
}

// Export singleton instance
export const paperTradingApi = new PaperTradingApiService();
export default paperTradingApi;
